// IE Hub - App Shell Component
// Main application shell with providers and layout

import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

// Providers
import ThemeProvider from "./providers/ThemeProvider.tsx";
import EngineProvider from "./providers/EngineProvider.tsx";
import AlertProvider from "./providers/AlertProvider.tsx";
import { HelpProvider } from "@/shared/help/HelpProvider";
import {
  UNAUTHORIZED_EVENT,
  type UnauthorizedEventDetail,
} from "@/shared/constants/events";

// UI Components
import { Box, CircularProgress, CssBaseline, Typography } from "@ui/index";

// Hooks
import { useScrollToTop } from "../shared/hooks/useScrollToTop.ts";
import { useAuth } from "../shared/state/store";

import ReciteMeButton from "@/components/ReciteMeButton.tsx";
import { ConfirmDialogHost } from "@/components/ConfirmDialog";
import { emitGlobalAlert } from "@/shared/lib/alertEvents";
import { consumeLogoutSource } from "@/shared/lib/logoutSource";
import SessionExpiryManager from "./SessionExpiryManager";
import NavigationLogger from "./NavigationLogger";
import { initMarketingTags, trackPageView } from "@/shared/tracking/initMarketingTags";
import SimpleLoadingScreen from "@/components/SimpleLoadingScreen";
import { useDOMErrorSuppression } from "@/shared/hooks/useDOMErrorSuppression";

const PUBLIC_ROUTE_PREFIXES: readonly string[] = ["/auth"];
const DEFAULT_UNAUTHORIZED_MESSAGE =
  "Your session has expired. Please sign in again.";

function AppShell(): JSX.Element {
  useDOMErrorSuppression(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { initializeAuth, isLoading, isAuthenticated, logout } = useAuth();
  const [authInitialized, setAuthInitialized] = useState(false);
  const unauthorizedHandledRef = useRef(false);

  const isProtectedRoute = useMemo(() => {
    const pathname = location.pathname;
    return !PUBLIC_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
  }, [location.pathname]);

  // Initialize auth on app startup
  useEffect(() => {
    const initAuth = async () => {
      await initializeAuth();
      setAuthInitialized(true);
    };
    initAuth();
  }, [initializeAuth]);

  // Initialize GTM/GA4/Meta Pixel once (safe to call multiple times)
  useEffect(() => {
    initMarketingTags();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return () => undefined;
    }

    const listener = (event: Event) => {
      if (!isProtectedRoute) {
        return;
      }

      const logoutSource = consumeLogoutSource();
      if (logoutSource === "manual" || logoutSource === "account_locked") {
        return;
      }

      if (unauthorizedHandledRef.current) {
        return;
      }

      const unauthorizedEvent = event as CustomEvent<
        UnauthorizedEventDetail | undefined
      >;
      const reason =
        unauthorizedEvent.detail?.reason ?? DEFAULT_UNAUTHORIZED_MESSAGE;

      unauthorizedHandledRef.current = true;

      void (async () => {
        try {
          await logout();
        } catch (error) {
          console.warn("Failed to logout after unauthorized response", error);
        }

        // Show a single, high-severity session expiry message (red)
        emitGlobalAlert({
          severity: "error",
          message: reason,
          autoHide: true,
          duration: 4000,
        });

        navigate("/auth", {
          replace: true,
          state: {
            from: `${location.pathname}${location.search}`,
          },
        });
      })();
    };

    window.addEventListener(UNAUTHORIZED_EVENT, listener as EventListener);
    return () => {
      window.removeEventListener(UNAUTHORIZED_EVENT, listener as EventListener);
    };
  }, [
    isProtectedRoute,
    location.pathname,
    location.search,
    logout,
    navigate,
  ]);

  useEffect(() => {
    if (isAuthenticated) {
      unauthorizedHandledRef.current = false;
    }
  }, [isAuthenticated]);

  // Scroll to top on route change
  useScrollToTop(location.pathname);

  // Update page title based on route
  useEffect(() => {
    const updateTitle = () => {
      const pathSegments = location.pathname.split("/").filter(Boolean);
      const pageTitle =
        pathSegments.length > 0
          ? pathSegments
              .map(
                (segment) => segment.charAt(0).toUpperCase() + segment.slice(1)
              )
              .join(" > ")
          : "Dashboard";

      document.title = `${pageTitle} - IE Hub`;
    };

    updateTitle();
  }, [location.pathname]);

  // Track SPA virtual page views on route changes (HashRouter friendly)
  useEffect(() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const pageTitle =
      pathSegments.length > 0
        ? pathSegments
            .map(
              (segment) => segment.charAt(0).toUpperCase() + segment.slice(1)
            )
            .join(" > ")
        : "Dashboard";

    trackPageView({
      pagePath: `${location.pathname}${location.search}`,
      pageTitle: `${pageTitle} - IE Hub`,
      pageLocation: typeof window !== "undefined" ? window.location.href : "",
    });
  }, [location.pathname, location.search]);

  return (
    <ThemeProvider>
      <CssBaseline />
      {/* Ensure loading UI is always themed (prevents default MUI blue fallback). */}
      {!authInitialized || isLoading ? (
        <SimpleLoadingScreen title="Loading..." />
      ) : (
        <HelpProvider>
          <EngineProvider>
            <AlertProvider>
              <NavigationLogger />
              <ConfirmDialogHost />
              <ReciteMeButton />
              <SessionExpiryManager />
              <Box data-testid="app-app-shell-container-action-1"
                sx={{
                  minHeight: "100vh",
                  display: "flex",
                  flexDirection: "column",
                  backgroundColor: "background.default",
                }}
              >
                {/* Global loading indicator, navigation, etc. could go here */}

                {/* Main content area */}
                <Box data-testid="app-app-shell-container-action-2"
                  component="main"
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Suspense
                    fallback={
                      <Box data-testid="app-app-shell-container-action-3"
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          minHeight: "50vh",
                          gap: 2,
                        }}
                      >
                        <CircularProgress size={40} />
                        <Typography data-testid="app-app-shell-text-loading..." variant="body1" color="text.secondary">
                          Loading...
                        </Typography>
                      </Box>
                    }
                  >
                    <Outlet />
                  </Suspense>
                </Box>

                {/* Global footer, modals, etc. could go here */}
              </Box>
            </AlertProvider>
          </EngineProvider>
        </HelpProvider>
      )}
    </ThemeProvider>
  );
}

export default AppShell;
